import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../shared/constants/routes';
import { api } from '../../shared/utils/api';
import AskAlex from '../topics/AskAlex';
import { formatExplanation } from '../../shared/utils/formatExplanation';
import { cleanMisconception } from '../../shared/utils/misconception';

export default function QuestionResult({ question, result, onNext, isLast }) {
  const navigate = useNavigate();
  const { correct, correct_answer, explanation, distractor_misconceptions, selected_answer } = result;
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('wrong_answer');
  const [reportDetail, setReportDetail] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [reportSending, setReportSending] = useState(false);

  const handleReport = async () => {
    if (reportSending) return;
    setReportSending(true);
    try {
      await api(`/api/questions/${question.id}/report`, {
        method: 'POST',
        body: JSON.stringify({ reason: reportReason, detail: reportDetail }),
      });
      setReportSent(true);
      setReportOpen(false);
    } catch {
      // silently fail
    } finally {
      setReportSending(false);
    }
  };
  const options = question.options || [];

  const [showMisconceptions, setShowMisconceptions] = React.useState(() => {
    try { return localStorage.getItem('sqetrack_show_misconceptions') !== 'false'; }
    catch { return true; }
  });
  const toggleMisconceptions = () => {
    const next = !showMisconceptions;
    setShowMisconceptions(next);
    try { localStorage.setItem('sqetrack_show_misconceptions', next ? 'true' : 'false'); } catch {}
  };

  const selectedMisconception = (() => {
    if (correct) return null;
    if (!distractor_misconceptions || typeof distractor_misconceptions !== 'object') return null;
    return distractor_misconceptions[selected_answer] || null;
  })();

  const topicsRoute = question.module === 'FLK2' ? ROUTES.FLK2 : ROUTES.FLK1;

  return (
    <div className="qp-card">
      {question.scenario && (
        <div className="qp-scenario">{question.scenario}</div>
      )}

      <div className="qp-stem qp-stem-nopad-bottom">{question.stem}</div>

      <div className="qp-options">
        {(() => {
          const correctU = (correct_answer || '').toString().toUpperCase();
          const selectedU = (selected_answer || '').toString().toUpperCase();
          return options.map(opt => {
            const letter = opt.letter;
            const letterU = (letter || '').toString().toUpperCase();
            const isCorrect = letterU === correctU;
            const isWrong = letterU === selectedU && !correct;
            let cls = 'qp-option';
            if (isCorrect) cls += ' qp-option-correct';
            else if (isWrong) cls += ' qp-option-incorrect';
            return (
              <button key={letter} className={cls} disabled>
                <span className="qp-option-letter">{letter}</span>
                <span className="qp-option-text">{opt.text}</span>
              </button>
            );
          });
        })()}
      </div>

      <div className="qp-result-banner-wrap">
        <div className={`qp-result-banner ${correct ? 'qp-result-correct' : 'qp-result-incorrect'}`}>
          {correct ? 'Correct' : 'Incorrect'}
        </div>
      </div>

      {selectedMisconception && (
        <div className="qp-misconception-wrap">
          <div className="qp-misconception-header">
            <span className="qp-misconception-label">Common misconception</span>
            <button className="qp-misconception-toggle" onClick={toggleMisconceptions}>
              {showMisconceptions ? 'Hide' : 'Show'}
            </button>
          </div>
          {showMisconceptions && (
            <div className="qp-misconception-body">{cleanMisconception(selectedMisconception)}</div>
          )}
        </div>
      )}

      <div className="qp-explanation">
        <div className="qp-explanation-label">Explanation</div>
        <div className="qp-explanation-text">
          {formatExplanation(explanation).map(p => (
            <p key={p.key} className="qp-explanation-para" dangerouslySetInnerHTML={{ __html: p.__html }} />
          ))}
        </div>
      </div>

      <div className="qp-breadcrumb">
        <span>{question.subject_area}</span>
        {question.general_topic && <><span>›</span><span>{question.general_topic}</span></>}
        <span>›</span>
        <span>{question.subtopic}</span>
      </div>

      <div className="qp-result-actions">
        <button className="btn-primary" onClick={onNext}>
          {isLast ? 'View summary' : 'Next question'}
        </button>
        <button
          className="qp-view-topic-link"
          onClick={() => navigate(topicsRoute)}
        >
          View this topic
        </button>
      </div>

      {!correct && (
        <AskAlex
          questionId={question.id}
          wasCorrect={false}
          initialMessage={`I got this question wrong: "${question.stem}". I chose ${selected_answer} but the correct answer was ${correct_answer}. Can you explain why?`}
        />
      )}

      <div className="qp-report-section">
        {reportSent ? (
          <span className="qp-report-sent">Thanks for reporting</span>
        ) : reportOpen ? (
          <div className="qp-report-form">
            <select
              className="qp-report-select"
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
            >
              <option value="wrong_answer">Wrong answer marked correct</option>
              <option value="confusing_wording">Confusing wording</option>
              <option value="outdated_law">Outdated law</option>
              <option value="other">Other</option>
            </select>
            <textarea
              className="qp-report-textarea"
              placeholder="Optional: add more detail"
              value={reportDetail}
              onChange={e => setReportDetail(e.target.value)}
              rows={2}
            />
            <div className="qp-report-actions">
              <button
                className="btn-primary"
                onClick={handleReport}
                disabled={reportSending}
              >
                {reportSending ? 'Sending' : 'Submit report'}
              </button>
              <button className="btn-ghost" onClick={() => setReportOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="qp-report-link" onClick={() => setReportOpen(true)}>
            Report this question
          </button>
        )}
      </div>
    </div>
  );
}
